import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Home, 
  ArrowLeft,
  MapPin,
  Truck,
  Package
} from 'lucide-react';

const NotFound = () => {
  return (
    <>
      <Helmet>
        <title>Page Non Trouvée - LST OIL SA | Logistique Pétrolière & Transport</title>
        <meta
          name="description"
          content="Page non trouvée. Retournez à l'accueil de LST OIL SA, leader en logistique pétrolière, transport et services industriels au Sénégal et en Afrique."
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://www.lstoilsa.com/" />
      </Helmet>


      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full text-center">
          {/* 404 Number */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, type: "spring" }}
            className="mb-8"
          >
            <div className="text-9xl md:text-[12rem] font-bold text-white opacity-20">
              404
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative z-10"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-heading">
              Oups !
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-2xl mx-auto">
              La page que vous recherchez n'existe pas ou a été déplacée. 
              Ne vous inquiétez pas, nous sommes là pour vous aider à retrouver votre chemin.
            </p>

            {/* Search Suggestion */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 mb-8">
              <h2 className="text-lg font-semibold text-white mb-4">
                Que recherchiez-vous ?
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { icon: Truck, label: 'Nos Services', link: '/services' },
                  { icon: Package, label: 'Nos Produits', link: '/products' },
                  { icon: MapPin, label: 'Nous Contacter', link: '/contact' }
                ].map((item, index) => (
                  <Link
                    key={index}
                    to={item.link}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-4 rounded-xl transition-all duration-300 transform hover:scale-105"
                  >
                    <item.icon className="w-8 h-8 mx-auto mb-2" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center gap-2"
              >
                <Home className="w-5 h-5" />
                Retour à l'Accueil
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Nous Contacter
              </Link>
            </div>

            {/* Help Text */}
            <div className="mt-12 text-blue-100">
              <p className="text-lg mb-4">
                Besoin d'aide ? Notre équipe est disponible pour vous accompagner
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  <span>Support 24/7</span>
                </div>
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  <span>Services personnalisés</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Floating Elements */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="absolute inset-0 pointer-events-none overflow-hidden"
          >
            <div className="absolute top-20 left-10 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
            <div className="absolute top-40 right-20 w-3 h-3 bg-blue-300 rounded-full animate-pulse delay-1000"></div>
            <div className="absolute bottom-40 left-20 w-2 h-2 bg-white rounded-full animate-pulse delay-500"></div>
            <div className="absolute bottom-20 right-10 w-3 h-3 bg-yellow-300 rounded-full animate-pulse delay-1500"></div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default NotFound;

